import React, { useEffect, useState } from "react";
import usePrivateProductImage, { useProductImageSession } from "../hooks/usePrivateProductImage";
import { getProductAssetUrl, invalidateProductAssetUrls, productImageSession, queueProductImageRequest } from "../services/productAssetUrls";
import { FiBox } from "react-icons/fi";
import axios from "../../api/axiosInstance";
import { readStoredUser } from "../../utils/sessionUser";
import { subscribeToCrossTabDataChanges } from "../../utils/crossTabDataSync";

// Cards and cart share bounded, short-lived previews within the same session.
// No signed URL is written into an item, cart line or requisition payload.
const covers = new Map();

let cacheScope = "";
const identity = productImageSession;
function loadCover(itemId, scope, user, force = false) {
  if (force && !covers.get(itemId)?.pending) covers.delete(itemId);
  if (cacheScope !== scope) { covers.clear(); cacheScope = scope; }
  const cached = covers.get(itemId);
  if (cached && cached.expiresAt > Date.now()) return cached.promise;
  const entry = { pending: true, expiresAt: Date.now() + 240000 };
  const config = {
    headers: { "X-Pilcore-Business-Unit-Id": String(user.active_business_unit_id) },
    pilcoreExpectedUserId: user.id,
  };
  entry.promise = (async () => {
    // Only current, published item details are read; pending galleries stay private.
    const response = await queueProductImageRequest(() => axios.get(`/api/purchasing/items/${itemId}`, config), scope);
    const item = response.data;
    if (Number(item?.id) !== Number(itemId) || (item.business_unit_id != null && Number(item.business_unit_id) !== Number(user.active_business_unit_id))) throw new Error("Item scope changed");
    if (identity() !== scope) throw new Error("Session changed");
    const photos = item.product_details?.photos || [];
    const cover = photos.find((photo) => photo.cover === true) || photos[0];
    if (!cover?.asset_id) return { url: null, expiresAt: entry.expiresAt };
    const result = await getProductAssetUrl(cover.asset_id, { config, context: { item_id: itemId }, variant: "thumbnail", force });
    if (identity() !== scope) throw new Error("Session changed");
    entry.expiresAt = result.expiresAt;
    return result;
  })().catch((error) => { if (covers.get(itemId) === entry) covers.delete(itemId); throw error; });
  entry.promise = entry.promise.finally(() => { entry.pending = false; });
  covers.set(itemId, entry);
  while (covers.size > 100) covers.delete(covers.keys().next().value);
  return entry.promise;
}

export default function MarketplaceProductImage({ item, text, compact = false }) {
  const scope = useProductImageSession(), [revision, setRevision] = useState(0);
  const itemId = Number(item?.id);
  const user = readStoredUser();
  const image = usePrivateProductImage({ identity: JSON.stringify([scope, itemId, revision]), enabled: Boolean(user?.id && user?.active_business_unit_id && itemId > 0),
    load: ({ force }) => loadCover(itemId, scope, user, force) });
  useEffect(() => {
    const changed = (event) => {
      if (/\/api\/(?:purchasing\/(?:items|catalog-creation-approvals)(?:\/|$)|admin\/purchasing\/catalog(?:\/|$))/.test(String(event.url || "").split("?")[0])) {
        covers.clear(); invalidateProductAssetUrls(); setRevision((value) => value + 1);
      }
    };
    return subscribeToCrossTabDataChanges(changed);
  }, []);
  return <span ref={image.ref} className="mp-imageMount">{image.url ? <img src={image.url} alt={item?.name || ""} loading={image.loading} decoding="async" draggable={false} onError={image.onError} />
    : <><FiBox aria-hidden="true" />{!compact && <small>{text("Sin fotografía", "No photograph")}</small>}</>}</span>;
}
